import { Text } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import React from 'react';
import {
  BasicActionIcon,
  IBasicActionIconProps,
} from 'src/design/atoms/BasicActionIcon';
import { DeleteButton } from 'src/design/atoms/DeleteButton';
import { useConfirmableAction } from 'src/design/molecules/useConfirmableAction';
import { Modal } from 'src/design/molecules/Modal';

export type IDeleteActionIconProps = Omit<
  IBasicActionIconProps,
  'onClick' | 'children'
> & {
  requiredConfirmation?: boolean;
  confirmationTitle?: string;
  confirmationText?: string;
  onDelete: () => void | Promise<void>;
};

const DefaultProps: Partial<IDeleteActionIconProps> = {
  variant: 'light',
  color: 'red',
};

/** @registry Red "delete" action icon that opens a confirmation Modal before calling onDelete; set requiredConfirmation=false to skip the prompt. */
export const DeleteActionIcon = React.forwardRef<
  HTMLButtonElement,
  IDeleteActionIconProps
>(
  (
    {
      requiredConfirmation = true,
      confirmationTitle = 'Are you sure?',
      confirmationText = 'Are you sure you want to delete this item?',
      onDelete,
      ...props
    },
    ref,
  ) => {
    const { opened, loading, requestOrRun, confirm, close } =
      useConfirmableAction(onDelete, requiredConfirmation);
    return (
      <>
        <BasicActionIcon
          {...DefaultProps}
          {...props}
          ref={ref}
          onClick={() => requestOrRun()}
        >
          <IconTrash size={20} />
        </BasicActionIcon>
        <Modal
          title={confirmationTitle}
          opened={opened}
          onClose={close}
          actions={[
            <DeleteButton
              key="delete"
              loading={loading}
              onClick={() => confirm()}
            />,
          ]}
        >
          <Text>{confirmationText}</Text>
        </Modal>
      </>
    );
  },
);
DeleteActionIcon.displayName = 'DeleteActionIcon';
